import GlobalTable from "@/components/probaworkflows_base_components/GlobalTable/GlobalTable.vue";
import NavigationBackButton from "@/components/probaworkflows_base_components/NavigationBackButton/NavigationBackButton.vue";
import { get_project_id, get_headers } from "@/utils/constants/constants.js";
import alert_mixin from "@/utils/mixins/alert_mixin";
import docuseal_mixin from "@/utils/mixins/docuseal_mixin";
import contract_management_mixin from "@/utils/mixins/contract_management_mixin";
import signer_management_mixin from "@/utils/mixins/signer_management_mixin";

export default {
  name: "InvoiceOverview",
  mixins: [
    alert_mixin,
    docuseal_mixin,
    contract_management_mixin,
    signer_management_mixin,
  ],
  props: {
    application_id: {
      type: [String, Number],
      default: null,
    },
    form_id: {
      type: [String, Number],
      default: null,
    },
    profile_role_id: {
      type: [String, Number],
      default: null,
    },
    role_id: {
      type: [String, Number],
      default: null,
    },
    user_id: {
      type: [String, Number],
      default: null,
    },
  },
  components: {
    GlobalTable,
    NavigationBackButton,
  },
  data: () => ({
    verification_data: [],
    preview_contract_data: null,
    reference_number: "",
    notes: "",
    show_preview_modal: false,
    show_masters_office_modal: false,
    loading_templates: false,
    headers: get_headers(),
    project_id: get_project_id(),
    has_scrolled_to_bottom: false,
    scroll_debounce: null,
    received_user_id: null,
    founder_name: "",
    received_application_id: null,
    received_form_id: null,
    received_role_id: null,
    received_profile_role_id: null,
    accordion_open: new Map(),
    preview_section_open: true,
    signers_section_open: true,
    timeline_section_open: true,
    send_button_text: "Send to Founder",
  }),

  computed: {
    is_pending_contract() {
      if (!this.verification_data || !Array.isArray(this.verification_data)) {
        return false;
      }
      return (
        this.preview_contract_data?.status === "pending" ||
        this.verification_data.some(
          (item) =>
            item.id === this.received_form_id && item.status === "pending"
        )
      );
    },

    show_timeline() {
      return this.timeline.length > 0 || this.contract_already_sent;
    },

    is_signable() {
      return this.is_pending_contract && !this.contract_already_sent;
    },
  },

  created() {
    this.received_application_id = this.application_id;
    this.received_form_id = this.form_id;
    this.received_role_id = this.role_id;
    this.received_profile_role_id = this.profile_role_id;
    this.received_user_id = this.user_id;
    this.founder_name = sessionStorage.getItem("user_name") || "You";
  },

  mounted() {
    if (this.verification_data && this.verification_data.length) {
      this.verification_data.forEach((_, index) => {
        this.accordion_open.set(index, false);
      });
    }
    this.get_assigned_templates();
    this.init_modal_events();
  },

  beforeDestroy() {
    if (this.docuseal_form_instance) {
      this.docuseal_form_instance.$destroy();
    }
    clearTimeout(this.scroll_debounce);
    this.remove_modal_events();
  },

  methods: {
    get_status_class(status) {
      if (!status) return "secondary";

      const status_map = new Map([
        ["COMPLIANT", "success"],
        ["NON_COMPLIANT", "danger"],
        ["GRACE_PERIOD", "info"],
        ["PENDING", "warning"],
        ["SIGNED", "success"],
        ["UNSIGNED", "warning"],
        ["DECLINED", "danger"],
        ["EXPIRED", "secondary"],
        ["APPROVED", "success"],
        ["REJECTED", "danger"],
      ]);

      const normalized_status = status
        .toString()
        .toUpperCase()
        .replace(/[\s-]/g, "_");
      return status_map.get(normalized_status) || "secondary";
    },

    toggle_accordion(index) {
      const new_state = new Map(this.accordion_open);
      new_state.set(index, !new_state.get(index));
      this.accordion_open = new_state;
    },

    toggle_preview_section() {
      this.preview_section_open = !this.preview_section_open;
      this.current_step = 1;
    },

    toggle_signers_section() {
      this.signers_section_open = !this.signers_section_open;
      this.current_step = this.signers_section_open ? 2 : 1;
    },

    toggle_timeline_section() {
      this.timeline_section_open = !this.timeline_section_open;
      this.current_step = this.timeline_section_open ? 3 : 2;
    },

    init_modal_events() {
      document.addEventListener("click", this.handle_backdrop_click);
      document.addEventListener("keydown", this.handle_keydown);
    },

    remove_modal_events() {
      document.removeEventListener("click", this.handle_backdrop_click);
      document.removeEventListener("keydown", this.handle_keydown);
    },

    handle_backdrop_click(event) {
      if (this.show_preview_modal && event.target.classList.contains("modal")) {
        this.close_preview_modal();
      }
    },

    handle_keydown(event) {
      if (event.key === "Escape" && this.show_preview_modal) {
        this.close_preview_modal();
      }
    },

    open_preview_modal() {
      this.show_preview_modal = true;
      document.body.classList.add("modal-open");
      this.$nextTick(() => {
        const viewer = this.$refs.docusealViewer;
        if (viewer) {
          viewer.addEventListener("scroll", this.handle_scroll);
        }
      });
    },

    close_preview_modal() {
      this.show_preview_modal = false;
      this.is_signing_mode = false;
      document.body.classList.remove("modal-open");
      const viewer = this.$refs.docusealViewer;
      if (viewer) {
        viewer.removeEventListener("scroll", this.handle_scroll);
      }
      if (this.docuseal_form_instance) {
        this.docuseal_form_instance.$destroy();
        this.docuseal_form_instance = null;
      }
    },

    preview_contract(row, signing = false) {
      if (!row) return;

      this.is_loading_document = true;
      this.document_loaded = false;
      this.is_signing_mode = signing;
      this.preview_contract_data = { ...row };
      this.template_id = row.id || null;
      this.open_preview_modal();
      this.contract_already_sent = row.was_sent || row.status === "sent";
      this.load_template_preview(signing);
      this.fetch_contract_status();

      if (this.last_submission_data) {
        this.restore_submission_data(this.last_submission_data);
      }
    },

    sign_contract(row) {
      this.preview_contract(row, true);
    },

    handle_scroll() {
      clearTimeout(this.scroll_debounce);
      this.scroll_debounce = setTimeout(() => {
        const viewer = this.$refs.docusealViewer;
        if (viewer) {
          const scroll_top = viewer.scrollTop;
          const scroll_height = viewer.scrollHeight;
          const client_height = viewer.clientHeight;
          const bottom_threshold = 50;

          this.has_scrolled_to_bottom =
            scroll_top + client_height >= scroll_height - bottom_threshold;
        }
      }, 100);
    },

    close_modal(modal) {
      if (modal === "preview") {
        this.close_preview_modal();
      } else if (modal === "masters") {
        this.show_masters_office_modal = false;
      }
    },

    get_current_user_email() {
      if (this.$store?.state?.user) {
        return this.$store.state.user.email || "";
      }
      return (
        this.user_email ||
        this.founder_email ||
        this.preview_contract_data?.email ||
        this.current_user?.email
      );
    },
  },
};
